import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { portfolioApi, type DecisaoRevisao } from '../api/portfolio.js';
import { VazioQueEnsina } from '../componentes/transversais/VazioQueEnsina.js';
import { SandboxCenario } from '../componentes/portfolio/SandboxCenario.js';
import { PainelRevisao } from '../componentes/portfolio/PainelRevisao.js';

function BarraTeto({ usoH, tetoH }: { usoH: number; tetoH: number }) {
  const percentual = tetoH === 0 ? 0 : Math.min(100, (usoH / tetoH) * 100);
  const excedeu = usoH > tetoH;
  return (
    <div>
      <div className="flex justify-between text-xs text-grafite">
        <span>Capacidade usada</span>
        <span className="fonte-mono">
          {usoH.toFixed(0)}h / {tetoH.toFixed(0)}h
        </span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-borda">
        <div className={`h-full ${excedeu ? 'bg-status-vermelho' : 'bg-aco'}`} style={{ width: `${percentual}%` }} />
      </div>
    </div>
  );
}

// SPEC-UX §4.9 — barra de teto sempre visível, revisões de continuidade
// obrigatórias fixadas no topo (R8.3), sandbox de cenários (§3.8) que
// promove a decisão para o painel de revisão formal com decisão por linha
// e justificativa obrigatória (R8.5).
export function PortfolioPagina() {
  const { data: portfolios, isLoading } = useQuery({ queryKey: ['portfolios'], queryFn: portfolioApi.listar });
  const [portfolioId, setPortfolioId] = useState<string | null>(null);
  const [painelAberto, setPainelAberto] = useState(false);
  const [decisoesPromovidas, setDecisoesPromovidas] = useState<Record<string, DecisaoRevisao>>({});

  const idAtivo = portfolioId ?? portfolios?.[0]?.id ?? null;

  const { data: teto } = useQuery({ queryKey: ['portfolio', idAtivo, 'teto'], queryFn: () => portfolioApi.teto(idAtivo!), enabled: !!idAtivo });
  const { data: consolidado } = useQuery({
    queryKey: ['portfolio', idAtivo, 'consolidado'],
    queryFn: () => portfolioApi.consolidado(idAtivo!),
    enabled: !!idAtivo,
  });
  const { data: continuidade } = useQuery({
    queryKey: ['portfolio', idAtivo, 'continuidade'],
    queryFn: () => portfolioApi.continuidade(idAtivo!),
    enabled: !!idAtivo,
  });

  if (isLoading) return <div className="p-8 text-sm text-grafite">Carregando…</div>;

  if (!portfolios || portfolios.length === 0) {
    return (
      <div className="p-8">
        <h1 className="mb-4 text-xl font-semibold text-ink">Portfólio</h1>
        <VazioQueEnsina explicacao="Nenhum portfólio criado. Portfólios agrupam programas e projetos sob um teto de capacidade comum." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink">Portfólio</h1>
        <div className="flex items-center gap-2">
          <select value={idAtivo ?? ''} onChange={(e) => setPortfolioId(e.target.value)} className="rounded border border-borda bg-base-100 px-2 py-1.5 text-sm text-ink">
            {portfolios.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))}
          </select>
          <button type="button" onClick={() => setPainelAberto(true)} className="rounded bg-aco px-3 py-1.5 text-xs font-medium text-white hover:bg-aco-700">
            Executar revisão
          </button>
        </div>
      </div>

      {teto && <BarraTeto usoH={teto.usoH} tetoH={teto.tetoH} />}

      {continuidade && continuidade.length > 0 && (
        <section className="rounded border border-status-vermelho/30 bg-status-vermelho/5 p-4">
          <h2 className="mb-2 text-sm font-semibold text-status-vermelho">Revisões de continuidade pendentes</h2>
          <ul className="flex flex-col gap-1">
            {continuidade.map((c) => (
              <li key={`${c.iniciativaTipo}_${c.iniciativaId}`} className="text-sm text-ink">
                {c.iniciativaTipo}: {c.iniciativaId}
              </li>
            ))}
          </ul>
        </section>
      )}

      {idAtivo && consolidado && (
        <SandboxCenario
          portfolioId={idAtivo}
          iniciativas={consolidado.iniciativas}
          onPromoverDecisao={(canceladas) => {
            setDecisoesPromovidas(Object.fromEntries(canceladas.map((id) => [id, 'Cancelar' as DecisaoRevisao])));
            setPainelAberto(true);
          }}
        />
      )}

      {painelAberto && idAtivo && consolidado && (
        <PainelRevisao
          portfolioId={idAtivo}
          iniciativas={consolidado.iniciativas}
          decisoesIniciais={decisoesPromovidas}
          onFechar={() => {
            setPainelAberto(false);
            setDecisoesPromovidas({});
          }}
        />
      )}

      <section>
        <h2 className="mb-2 text-sm font-semibold text-grafite">Iniciativas</h2>
        <ul className="flex flex-col gap-2">
          {consolidado?.iniciativas.map((ini) => (
            <li key={`${ini.tipo}_${ini.id}`} className="flex items-center justify-between rounded border border-borda bg-superficie px-4 py-3 text-sm">
              <span className="text-ink">
                {ini.tipo}: {ini.id}
              </span>
              <span className="fonte-mono text-xs text-grafite">{ini.estado}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
